import type { Signal } from '@angular/core';
import { computed, inject, Injectable } from '@angular/core';
import { UserProfileService } from '@features/user/services/user-profile.service';
import type { UserProfileUpdate } from '@features/user/types/user.types';
import type { ResponseUserProfileDto } from '@kerberos-compliance/compliance-shared-ts/features/user/dtos/response-user-profile.dto';
import type { AmlAdminDeskFeatureTable } from '@kerberos-compliance/compliance-shared-ts/features/user/enums/feature-table.enum';
import type {
  ColumnPreference,
  TablePreference,
  UserProfilePreferences,
  UserTablePreferences,
} from '@kerberos-compliance/compliance-shared-ts/features/user/types/user-profile.types';
import { TablePreferencesProvider, UserProfileStore } from '@kerberos-compliance/compliance-shared-ui';

@Injectable()
export class AmlAdminTablePreferenceProvider extends TablePreferencesProvider<AmlAdminDeskFeatureTable> {
  private readonly store = inject(UserProfileStore);

  private readonly userProfileService = inject(UserProfileService);

  /**
   * Returns a reactive signal containing the AML Admin Desk platform's table preferences slice.
   *
   * @returns {Signal<Partial<Record<AmlAdminDeskFeatureTable, TablePreference>>>} A signal containing the table preferences, or an empty object if none exist.
   */
  public getPlatformSlice(): Signal<Partial<Record<AmlAdminDeskFeatureTable, TablePreference>>> {
    return computed(() => this.store.allTablePreferences().amlAdminDesk || {});
  }

  /**
   * Persists the user preferences to the backend via HTTP PATCH.
   *
   * @param {UserProfilePreferences} userPreferences - The complete user preferences object to save, including the updated table preferences.
   * @returns {Promise<ResponseUserProfileDto>} A promise that resolves to the updated user profile.
   */
  public async savePreferencesToBackend(userPreferences: UserProfilePreferences): Promise<ResponseUserProfileDto> {
    return this.userProfileService.updateUserProfile({
      userPreferences,
    } satisfies UserProfileUpdate);
  }

  /**
   * Merges new column preferences for a specific table into the existing preferences.
   *
   * @param {AmlAdminDeskFeatureTable} tableKey - The unique identifier for the table being updated.
   * @param {ColumnPreference[]} [newPreferences] - The new column preferences to save, or undefined to remove.
   * @returns {UserTablePreferences} The merged table preferences object.
   */
  public mergeWithExistingPreferences(
    tableKey: AmlAdminDeskFeatureTable,
    newPreferences?: ColumnPreference[],
  ): UserTablePreferences {
    const allTablePreferences = this.store.allTablePreferences();
    const tablePreferences = { ...allTablePreferences.amlAdminDesk };

    let newTablePreferences: Record<string, TablePreference>;

    if (newPreferences && newPreferences.length > 0) {
      newTablePreferences = {
        ...tablePreferences,
        [tableKey]: {
          ...tablePreferences[tableKey],
          columns: newPreferences,
        },
      };
    } else {
      const { [tableKey]: _removed, ...remainingTables } = tablePreferences;
      newTablePreferences = remainingTables;
    }

    return {
      ...allTablePreferences,
      amlAdminDesk: newTablePreferences,
    } satisfies UserTablePreferences;
  }
}
